import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, helpers } from '@vuelidate/validators';

const DEFAULT_ALLOWED_MIMES = [
  'image/jpeg',
  'image/png',
  'video/mp4',
  'video/3gpp',
  'application/pdf',
];

// Mirrors backend provider constraints
// (Whatsapp::MediaUploadService::SUPPORTED_FORMATS, Gupshup::PartnerMediaUploadService::FORMAT_LIMITS).
export const MEDIA_SIZE_LIMITS_MB = {
  image: 5,
  video: 16,
  document: 100,
};

export const getSizeLimitForFile = file => {
  const type = file?.type || '';
  if (type.startsWith('image/')) return MEDIA_SIZE_LIMITS_MB.image;
  if (type.startsWith('video/')) return MEDIA_SIZE_LIMITS_MB.video;
  if (type === 'application/pdf') return MEDIA_SIZE_LIMITS_MB.document;
  return MEDIA_SIZE_LIMITS_MB.document;
};

/**
 * Shared composable for Media template validation
 * Validates media_file (File) presence, MIME and per-format size.
 *
 * @param {Ref<Object>} mediaData - Ref to media data object with media_file (File) field
 * @param {ComputedRef<Object>} variableValidationRules - Variable validation rules
 * @param {ComputedRef<Object>} variableValidationState - Variable validation state
 * @param {Object} config - Platform configuration
 * @param {string} config.i18nPrefix - i18n key prefix (e.g., 'CONTENT_TEMPLATES' or 'WHATSAPP_TEMPLATES')
 * @param {Array<string>} config.allowedMimes - Allowed MIME types
 */
export function useMediaValidation(
  mediaData,
  variableValidationRules,
  variableValidationState,
  config = {}
) {
  const { t } = useI18n();

  const {
    i18nPrefix = 'CONTENT_TEMPLATES',
    allowedMimes = DEFAULT_ALLOWED_MIMES,
  } = config;

  const detectMediaTypeFromMime = mime => {
    if (!mime) return null;
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime === 'application/pdf') return 'document';
    return null;
  };

  const detectedMediaType = computed(() => {
    const file = mediaData.value?.media_file;
    return file ? detectMediaTypeFromMime(file.type) : null;
  });

  const mediaTypeLabel = computed(() => {
    if (!detectedMediaType.value)
      return t(`${i18nPrefix}.CREATE.MEDIA.UNKNOWN`);

    const types = {
      image: t(`${i18nPrefix}.CREATE.MEDIA.TYPES.IMAGE`),
      video: t(`${i18nPrefix}.CREATE.MEDIA.TYPES.VIDEO`),
      document: t(`${i18nPrefix}.CREATE.MEDIA.TYPES.DOCUMENT`),
    };
    return types[detectedMediaType.value];
  });

  const hasInvalidFileExtension = computed(() => {
    const file = mediaData.value?.media_file;
    return !!file && !detectedMediaType.value;
  });

  const validMime = helpers.withMessage(
    () => t(`${i18nPrefix}.CREATE.MEDIA.INVALID_FILE_TYPE`),
    value => {
      if (!value) return true;
      return allowedMimes.includes(value.type);
    }
  );

  const validSize = helpers.withMessage(
    ({ $model }) =>
      t(`${i18nPrefix}.CREATE.MEDIA.FILE_SIZE_ERROR`, {
        max: getSizeLimitForFile($model),
      }),
    value => {
      if (!value) return true;
      return value.size <= getSizeLimitForFile(value) * 1024 * 1024;
    }
  );

  const rules = computed(() => ({
    media_file: {
      required,
      validMime,
      validSize,
    },
    ...variableValidationRules.value,
  }));

  const validationState = computed(() => ({
    ...mediaData.value,
    ...variableValidationState.value,
  }));

  const getErrorMessage = (field, v$) => {
    if (field.startsWith('var_')) {
      return '';
    }

    const fieldValidator = v$.value[field];
    if (!fieldValidator?.$error) return '';

    if (fieldValidator.required?.$invalid) {
      return t(`${i18nPrefix}.CREATE.MEDIA.FIELD_REQUIRED`);
    }
    if (fieldValidator.validMime?.$invalid) {
      return t(`${i18nPrefix}.CREATE.MEDIA.INVALID_FILE_TYPE`);
    }
    if (fieldValidator.validSize?.$invalid) {
      return t(`${i18nPrefix}.CREATE.MEDIA.FILE_SIZE_ERROR`, {
        max: getSizeLimitForFile(mediaData.value?.media_file),
      });
    }

    return '';
  };

  return {
    rules,
    validationState,
    getErrorMessage,
    detectedMediaType,
    mediaTypeLabel,
    hasInvalidFileExtension,
  };
}
